'use client';

import { cn } from '@/lib/utils';
import type { AuroraData } from '@/lib/aurora';
import {
  resolveAuroraLabels,
  type AuroraLabels,
  type ResolvedAuroraLabels,
} from '@/components/aurora/labels';
import { motion } from 'framer-motion';

interface AuroraStatusBannerProps {
  data: AuroraData | null;
  loading?: boolean;
  labels?: AuroraLabels;
}

function getCloudStatus(cloudCover: number | null, L: ResolvedAuroraLabels): string {
  if (cloudCover === null) return L.cloudStatus.unavailable;
  if (cloudCover < 25) return L.cloudStatus.clear;
  if (cloudCover <= 50) return L.cloudStatus.partly;
  if (cloudCover <= 75) return L.cloudStatus.cloudy;
  return L.cloudStatus.overcast;
}

function getRelativeTime(updatedAt: string, L: ResolvedAuroraLabels): string {
  const now = Date.now();
  const updated = new Date(updatedAt).getTime();
  const diffMs = now - updated;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return L.time.justNow;
  if (diffMin === 1) return L.time.minuteAgo;
  if (diffMin < 60) return L.time.minutesAgo.replace('{count}', String(diffMin));
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours === 1) return L.time.hourAgo;
  return L.time.hoursAgo.replace('{count}', String(diffHours));
}

export default function AuroraStatusBanner({ data, loading, labels }: AuroraStatusBannerProps) {
  const L = resolveAuroraLabels(labels);
  if (loading) {
    return (
      <div
        className={cn(
          'w-full h-40 rounded-2xl animate-pulse',
          'bg-[#F0F0EB]',
        )}
      />
    );
  }

  if (!data) return null;

  const hasGlow = data.level === 'amber' || data.level === 'red';
  const levelText = L.levels[data.level];

  return (
    <motion.div
      className="relative w-full rounded-2xl overflow-hidden"
      style={{
        backgroundColor: `${data.hex}26`,
        borderLeft: `4px solid ${data.hex}`,
      }}
    >
      {hasGlow && (
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ boxShadow: `0 0 30px ${data.hex}40, inset 0 0 30px ${data.hex}10` }}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}

      <div className="relative p-6 md:p-8 space-y-4">
        {/* Header */}
        <p
          className={cn(
            'text-xs font-sans uppercase tracking-widest',
            'text-[#5A5A6E]',
          )}
        >
          {L.bannerEyebrow}
        </p>

        {/* Main status */}
        <h2
          className={cn(
            'font-sans text-2xl md:text-3xl font-light',
            'text-[#1A1A2E]',
          )}
        >
          {levelText.label} - {levelText.description}
        </h2>

        {/* Stats row */}
        <div
          className={cn(
            'flex flex-wrap gap-x-6 gap-y-1 text-sm',
            'text-[#1A1A2E]',
          )}
        >
          <span>{L.bannerKpLabel} {data.kp.current}</span>
          <span className="text-[#5A5A6E]">|</span>
          <span>{L.bannerRIndexLabel} {data.fmi.rIndex ?? '-'}</span>
          <span className="text-[#5A5A6E]">|</span>
          <span>{getCloudStatus(data.cloudCover, L)}</span>
        </div>

        {/* Guest advice */}
        <p
          className={cn(
            'text-sm italic',
            'text-[#5A5A6E]',
          )}
        >
          {levelText.advice}
        </p>

        {/* Updated time */}
        <p
          className={cn(
            'text-xs',
            'text-[#5A5A6E]',
          )}
        >
          {L.bannerUpdatedPrefix} {getRelativeTime(data.updatedAt, L)}
        </p>
      </div>
    </motion.div>
  );
}
